import React, { useState, useEffect, useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { wikiAPI } from '../../api/api';
import FilePreviewModal from './FilePreviewModal';
import FileUploadModal from './FileUploadModal';
import { sanitizeHtml } from '../../utils/sanitizeHtml';
import './Wiki.css';

const FileCard = ({ file, onPreview }) => {
  const getFileExtension = (filename) => {
    if (!filename) return '';
    return filename.split('.').pop().toUpperCase();
  };

  const truncateFileName = (filename, maxLength = 20) => {
    if (!filename) return '';
    if (filename.length <= maxLength) return filename;
    
    const extension = getFileExtension(filename);
    const nameWithoutExt = filename.slice(0, -(extension.length + 1));
    const availableLength = maxLength - extension.length - 4;
    
    if (availableLength <= 3) {
      return `...${extension}`;
    }
    
    return `${nameWithoutExt.slice(0, availableLength)}...${extension}`;
  };

  const getFileIconClass = (mimeType) => {
    if (!mimeType) return 'fa-file-alt';
    if (mimeType.includes('pdf')) return 'fa-file-pdf';
    if (mimeType.includes('word') || mimeType.includes('document')) return 'fa-file-word';
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return 'fa-file-excel';
    if (mimeType.includes('image')) return 'fa-file-image';
    if (mimeType.includes('zip') || mimeType.includes('compressed')) return 'fa-file-archive';
    if (mimeType.includes('text')) return 'fa-file-alt';
    return 'fa-file-alt';
  };

  return (
    <button 
      className="file-card"
      onClick={() => onPreview(file)}
      title={file.original_name || file.name}
    >
      <div className="file-card-icon">
        <i className={`fas ${getFileIconClass(file.mime_type)}`}></i>
      </div>
      <div className="file-card-info">
        <span className="file-card-name">
          {truncateFileName(file.original_name || file.name)}
        </span>
        <span className="file-card-size">
          {file.file_size ? `${(file.file_size / 1024).toFixed(1)} KB` : 'Unknown size'}
        </span>
      </div>
    </button>
  );
};

const FilesGrid = ({ files, onFilePreview }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const shouldShowExpandButton = files.length > 8;

  const handleExpandToggle = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className="files-container">
      <div 
        className={`files-grid ${shouldShowExpandButton && !isExpanded ? 'collapsed' : ''}`}
      >
        {files.map(file => (
          <FileCard 
            key={file.id} 
            file={file} 
            onPreview={onFilePreview}
          />
        ))}
      </div>
      
      {shouldShowExpandButton && (
        <button 
          className={`expand-files-btn ${isExpanded ? 'expanded' : ''}`}
          onClick={handleExpandToggle}
        >
          <i className={`fas ${isExpanded ? 'fa-chevron-up' : 'fa-chevron-down'}`}></i>
          {isExpanded ? 'Скрыть файлы' : 'Показать все файлы'}
        </button>
      )}
    </div>
  );
};

const WikiViewer = ({ document, onUpdate, canEdit, onDocumentUpdate }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [editForm, setEditForm] = useState({
    title: '',
    content: ''
  });

  const quillRef = useRef(null);

  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      ['link', 'image'],
      ['clean']
    ],
  };

  const quillFormats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'indent',
    'link', 'image'
  ];

  useEffect(() => {
    if (!document || (Object.keys(document).length === 1 && document.category)) {
      return;
    }
    
    if (document?.id) {
      loadFiles();
    }
  }, [document]);

  useEffect(() => {
    if (document && isEditing) {
      setEditForm({
        title: document.title || '',
        content: document.content || ''
      });
    }
  }, [document, isEditing]);

  const loadFiles = async () => {
    try {
      const response = await wikiAPI.getDocumentFiles(document.id);
      setFiles(response.files || []);
    } catch (error) {
      setFiles([]);
    }
  };

  const handleFileDeleted = (fileId) => {
    setFiles(prev => prev.filter(file => file.id !== fileId));
  };

  const handleSaveEdit = async () => {
    try {
      if (!editForm.title.trim()) {
        alert('Введите название документа');
        return;
      }

      if (editForm.title.length > 20) {
        alert('Название документа должно быть не более 20 символов');
        return;
      }

      if (!editForm.content.trim()) {
        alert('Введите содержимое документа');
        return;
      }

      const response = await wikiAPI.updateDocument(document.id, editForm);
      if (response.success) {
        const updatedDocumentResponse = await wikiAPI.getDocument(document.id);
        if (updatedDocumentResponse.success && onDocumentUpdate) {
          onDocumentUpdate(updatedDocumentResponse.document);
        }
        
        setIsEditing(false);
        onUpdate();
      } else {
        throw new Error(response.error);
      }
    } catch (error) {
      alert('Ошибка при сохранении');
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditForm({
      title: document?.title || '',
      content: document?.content || ''
    });
  };

const formatDateTime = (dateString) => {
  if (!dateString) return 'Неизвестно';
  
  try {
    const date = new Date(dateString);
    // Добавляем 3 часа для московского времени
    const moscowTime = new Date(date.getTime() + (3 * 60 * 60 * 1000));
    
    return moscowTime.toLocaleString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Неизвестно';
  }
};

  const handleFilePreview = (file) => {
    setPreviewFile(file);
  };

  if (!document) {
    return (
      <div className="wiki-viewer empty">
        <div className="empty-state">
          <i className="fas fa-file-alt"></i>
          <h3>Выберите документ</h3>
          <p>Выберите документ из списка слева для просмотра</p>
        </div>
      </div>
    );
  }

  return (
    <div className="wiki-viewer">
      <div className="viewer-header">
        <div className="document-info">
          {isEditing ? (
            <div className="edit-title">
              <input
                type="text"
                value={editForm.title}
                onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Название документа"
                maxLength={20}
                className="title-input"
              />
              <div className="char-counter">
                {editForm.title.length}/20 символов
              </div>
            </div>
          ) : (
            <h1>{document.title || document.name || 'Без названия'}</h1>
          )}
          <div className="document-meta">
            <span className="author">
              Автор: {document.author_name || document.created_by_username || 'admin'}
            </span>
            <span className="date">
              Обновлено: {formatDateTime(document.updated_at)}
            </span>
          </div>
        </div>
        
        <div className="document-actions">
          {canEdit && document.id && (
            <>
              {isEditing ? (
                <>
                  <button
                    className="exchange-btn"
                    onClick={() => setShowUploadModal(true)}
                  >
                    <i className="fas fa-upload"></i>
                    Загрузить файлы
                  </button>
                  <button 
                    className="add-button" 
                    onClick={handleSaveEdit}
                  >
                    <i className="fas fa-save"></i>
                    Сохранить
                  </button>
                </>
              ) : (
                <button 
                  className="add-button" 
                  onClick={() => setIsEditing(true)}
                >
                  <i className="fas fa-edit"></i>
                  Редактировать
                </button>
              )}
            </>
          )}
        </div>
      </div>

      <div className="document-content">
        {isEditing ? (
          <div className="editor-container">
            <ReactQuill
              ref={quillRef}
              value={editForm.content}
              onChange={(content) => setEditForm(prev => ({ ...prev, content }))}
              modules={quillModules}
              formats={quillFormats}
              placeholder="Введите содержимое документа..."
              theme="snow"
            />
          </div>
        ) : document.content ? (
          <div 
            className="ql-editor-content"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(document.content) }}
          />
        ) : (
          <div className="empty-content">
            <i className="fas fa-file-alt"></i>
            <h3>Документ пуст</h3>
            <p>Содержимое документа отсутствует</p>
            {canEdit && (
              <button 
                className="exchange-btn"
                onClick={() => setIsEditing(true)}
              >
                <i className="fas fa-edit"></i>
                Добавить содержимое
              </button>
            )}
          </div>
        )}
      </div>

      <div className="document-files">
        <h3>
          <i className="fas fa-paperclip"></i>
          Прикрепленные файлы ({files.length})
        </h3>
        {files.length > 0 ? (
          <FilesGrid 
            files={files}
            onFilePreview={handleFilePreview}
          />
        ) : (
          <p className="no-files">Нет прикрепленных файлов</p>
        )}
      </div>

      {previewFile && (
        <FilePreviewModal
          file={previewFile}
          onClose={() => setPreviewFile(null)}
          onFileDeleted={handleFileDeleted}
          canEdit={canEdit}
        />
      )}

      {showUploadModal && document && (
        <FileUploadModal
          documentId={document.id}
          onClose={() => setShowUploadModal(false)}
          onFilesUploaded={(uploadedFiles) => {
            setFiles(prev => [...prev, ...uploadedFiles]);
            setShowUploadModal(false);
          }}
        />
      )}
    </div>
  );
};

export default WikiViewer;
